import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Card, Grid, Heading, HStack, Icon, IconButton, Separator, SimpleGrid, Text, Textarea, VStack, chakra } from '@chakra-ui/react';
import { UserChip } from '@src/components/sco/Account/UserChip';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { InviteToBoardModal } from '@src/components/sco/Advisors/modals/InviteToBoardModal';
import { SessionRatingModal } from '@src/components/sco/Advisors/modals/SessionRatingModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { SessionChatPanel } from '@src/components/sco/Advisors/sessions/SessionChatPanel';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { SectionHeader } from '@src/components/sco/Advisors/shared/SectionHeader';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { SessionDeviceCheck } from '@src/components/sco/Advisors/shared/SessionDeviceCheckCard';
import { SuggestedTimesFields, useSuggestedTimes } from '@src/components/sco/Advisors/shared/SuggestedTimesFields';
import { bookingErrorCode } from '@src/components/sco/Advisors/utils/sessionBookFlow';
import { cleanErrorMessage, downloadTextFile, formatHourlyRate, formatUsd, formatWhen, formatWhenParts, personName, sessionMediaUrl } from '@src/components/sco/Advisors/utils/sessionFormat';
import { isJoinableSession, isSessionParty, isNegotiatingSession, isScheduledSession, isTerminalSession, isWaitingOnCurrentUser, SESSION_STATUS, sessionStatusPalette } from '@src/components/sco/Advisors/utils/sessionStatus';
import { attachedAsidePanelContainerProps, attachedPanelContainerProps, NestedAppLayout } from '@src/components/sco/AppLayout';
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { CANCEL_ADVISOR_SESSION } from '@src/gql/mutations/CancelAdvisorSession';
import { CONFIRM_ADVISOR_SESSION_SCHEDULE } from '@src/gql/mutations/ConfirmAdvisorSessionSchedule';
import { PROPOSE_ADVISOR_SESSION_SCHEDULE } from '@src/gql/mutations/ProposeAdvisorSessionSchedule';
import { ADVISOR_SESSION, ADVISOR_SESSION_MEDIA } from '@src/gql/queries/AdvisorSession';
import { ADVISORS_ATTENTION_REFETCH } from '@src/gql/queries/AdvisorsAttention';
import { ScoAdvisorSession, ScoUser } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { LuMessageSquare, LuPanelRightClose } from 'react-icons/lu';
import { LuArrowLeft, LuCalendarClock, LuCalendarPlus, LuCheck, LuClock, LuFileText, LuStar, LuVideo, LuX } from 'react-icons/lu';
type SessionQueryData = {
  advisorSession?: ScoAdvisorSession | null;
};

/** Completed-session next actions live in SessionDetailView, not the room. */

/** `recording` / `transcript` are JSON scalars on ScoAdvisorSession. */
type SessionMedia = {
  recording?: {
    url?: string;
    expiresAt?: string | null;
  } | null;
  transcript?: {
    url?: string;
  } | null;
  recordingPartyExpiresAt?: string | null;
};
type ExpertReceipt = {
  gross: number;
  platformFee: number;
  expertShare: number;
  feePercent: number;
  expertPercent: number;
};
type SessionDetailViewProps = {
  session: ScoAdvisorSession;
  media?: SessionMedia;
  advisorSessionId: string;
  sessionsHref: string;
  cancelReason: string;
  actionError: string | null;
  confirmLoading: boolean;
  proposeLoading: boolean;
  cancelLoading: boolean;
  onCancelReasonChange: (value: string) => void;
  onConfirm: (scheduledAt: string) => void;
  onPropose: (suggestedTimes: string[]) => void;
  onCancel: () => void;
  onRate: () => void;
  bookAgainHref?: string | null;
  opportunitiesHref: string;
  sessionSettingsHref: string;
  inboxHref?: string | null;
  canInviteToBoard?: boolean;
  onInviteToBoard?: () => void;
  /** L19 chat panel (session thread + Messages toggle), placed next to the scheduling card. */
  chatPanel?: ReactNode;
};
const toChipUser = (user: ScoAdvisorSession['expertUser'], fallback: string): ScoUser => ({
  ...(user ?? {}),
  id: user?.id || user?.userId || fallback,
  userId: user?.userId,
  displayName: personName(user, fallback),
  fullName: personName(user, fallback),
  pictureUrl: user?.pictureUrl
}) as ScoUser;
const DetailCard = t0 => {
  const $ = _c(10);
  const {
    label,
    description,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/55" textStyle="labelXs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = description && <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{description}</Text>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <VStack align="stretch" gap={1}>{t1}{t2}</VStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <Card.Root boxShadow="sm" variant="subtle"><Card.Body gap={4}>{t3}{children}</Card.Body></Card.Root>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const MetaItem = t0 => {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/50" textStyle="label2xs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <Box color="colorPalette.fg/90" fontFamily="longform" fontSize="sm" fontWeight="medium">{children}</Box>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <VStack align="stretch" gap={1} minWidth={0}>{t1}{t2}</VStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const ReceiptRow = t0 => {
  const $ = _c(12);
  const {
    label,
    value,
    emphasize
  } = t0;
  const t1 = emphasize ? "colorPalette.fg" : "colorPalette.fg/55";
  const t2 = emphasize ? "sm" : "xs";
  const t3 = emphasize ? "semibold" : "medium";
  let t4;
  if ($[0] !== label || $[1] !== t1 || $[2] !== t2 || $[3] !== t3) {
    t4 = <Text color={t1} fontSize={t2} fontWeight={t3}>{label}</Text>;
    $[0] = label;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = emphasize ? "lg" : "sm";
  const t6 = emphasize ? "semibold" : "medium";
  let t7;
  if ($[5] !== t5 || $[6] !== t6 || $[7] !== value) {
    t7 = <Text fontFamily="longform" fontSize={t5} fontWeight={t6} textAlign="end">{value}</Text>;
    $[5] = t5;
    $[6] = t6;
    $[7] = value;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <HStack align="baseline" gap={4} justify="space-between">{t4}{t7}</HStack>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
};
const PersonTile = t0 => {
  const $ = _c(16);
  const {
    label,
    user,
    fallback,
    isYou
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/50" textStyle="label2xs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isYou) {
    t2 = isYou && <Badge size="sm" variant="subtle">You</Badge>;
    $[2] = isYou;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <HStack justify="space-between">{t1}{t2}</HStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== fallback || $[8] !== user) {
    t4 = toChipUser(user, fallback);
    $[7] = fallback;
    $[8] = user;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      disablePresence: true,
      avatarProps: {
        size: "md"
      }
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4) {
    t6 = <UserChip user={t4} userAvatarProps={t5} />;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = <VStack align="stretch" bg="colorPalette.card/50" borderColor="colorPalette.border/60" borderRadius="l2" borderWidth="1px" gap={3} minWidth={0} padding={3}>{t3}{t6}</VStack>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
type ProposeTimesFormProps = {
  session: ScoAdvisorSession;
  canConfirm: boolean;
  waitingOnCounterpart: boolean;
  counterpartLabel: string;
  loading: boolean;
  onPropose: (suggestedTimes: string[]) => void;
};
const proposeTimesDescription = ({
  canConfirm,
  waitingOnCounterpart,
  counterpartLabel
}: Pick<ProposeTimesFormProps, 'canConfirm' | 'waitingOnCounterpart' | 'counterpartLabel'>) => {
  if (canConfirm) {
    return 'Offer up to three alternatives instead of accepting a proposed slot.';
  }
  if (waitingOnCounterpart) {
    return `You can revise your availability; the ${counterpartLabel} will need to accept.`;
  }
  return 'Suggest up to three times that work for you.';
};

/**
 * Propose-times fields. Mounted only once the session has loaded so the inputs
 * can seed straight from server data — no hydration flag, no effect.
 */
const ProposeTimesForm = t0 => {
  const $ = _c(22);
  const {
    session,
    canConfirm,
    waitingOnCounterpart,
    counterpartLabel,
    loading,
    onPropose
  } = t0;
  let t1;
  if ($[0] !== session.suggestedTime1 || $[1] !== session.suggestedTime2 || $[2] !== session.suggestedTime3) {
    t1 = [session.suggestedTime1, session.suggestedTime2, session.suggestedTime3];
    $[0] = session.suggestedTime1;
    $[1] = session.suggestedTime2;
    $[2] = session.suggestedTime3;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const times = useSuggestedTimes(t1);
  let t2;
  if ($[4] !== canConfirm || $[5] !== counterpartLabel || $[6] !== waitingOnCounterpart) {
    t2 = proposeTimesDescription({
      canConfirm,
      waitingOnCounterpart,
      counterpartLabel
    });
    $[4] = canConfirm;
    $[5] = counterpartLabel;
    $[6] = waitingOnCounterpart;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const description = t2;
  let t3;
  if ($[8] !== description) {
    t3 = <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{description}</Text>;
    $[8] = description;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== times) {
    t4 = <SuggestedTimesFields label="Your proposed times" times={times} />;
    $[10] = times;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== onPropose || $[13] !== times.isoValues) {
    t5 = () => onPropose(times.isoValues);
    $[12] = onPropose;
    $[13] = times.isoValues;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== loading || $[16] !== t5) {
    t6 = <Button alignSelf="flex-start" loading={loading} variant="outline" onClick={t5}>Send proposed times</Button>;
    $[15] = loading;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t3 || $[19] !== t4 || $[20] !== t6) {
    t7 = <VStack align="stretch" gap={4}>{t3}{t4}{t6}</VStack>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
const ProposeTimesCard = props => {
  const $ = _c(5);
  const t0 = props.canConfirm ? "Propose different times" : "Propose new times";
  let t1;
  if ($[0] !== props) {
    t1 = <ProposeTimesForm {...props} />;
    $[0] = props;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <DetailCard label={t0}>{t1}</DetailCard>;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const SessionDetailView = t0 => {
  const $ = _c(202);
  const {
    session,
    media,
    advisorSessionId,
    sessionsHref,
    cancelReason,
    actionError,
    confirmLoading,
    proposeLoading,
    cancelLoading,
    onCancelReasonChange,
    onConfirm,
    onPropose,
    onCancel,
    onRate,
    bookAgainHref,
    opportunitiesHref,
    sessionSettingsHref,
    inboxHref,
    canInviteToBoard,
    onInviteToBoard,
    chatPanel
  } = t0;
  const statusKey = session.statusKey;
  const role = session.currentUserRole;
  let t1;
  if ($[0] !== role) {
    t1 = isSessionParty(role);
    $[0] = role;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isParty = t1;
  let t2;
  if ($[2] !== statusKey) {
    t2 = isTerminalSession(statusKey);
    $[2] = statusKey;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isTerminal = t2;
  let t3;
  if ($[4] !== statusKey) {
    t3 = isNegotiatingSession(statusKey);
    $[4] = statusKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isNegotiating = t3;
  let t4;
  if ($[6] !== statusKey) {
    t4 = isScheduledSession(statusKey);
    $[6] = statusKey;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isScheduled = t4;
  const isCompleted = statusKey === SESSION_STATUS.completed;
  let t5;
  if ($[8] !== role || $[9] !== statusKey) {
    t5 = isWaitingOnCurrentUser(statusKey, role);
    $[8] = role;
    $[9] = statusKey;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const canConfirm = t5;
  const waitingOnCounterpart = isNegotiating && !canConfirm;
  const counterpartLabel = role === "EXPERT" ? "founder" : "advisor";
  const canRate = !!session.canRate;
  const myRating = session.currentUserRating;
  const ics = session.ics as {
    fileName?: string;
    content?: string;
  } | null | undefined;
  const t6 = media?.recording;
  let t7;
  if ($[11] !== t6) {
    t7 = sessionMediaUrl(t6);
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const recordingUrl = t7;
  const t8 = media?.transcript;
  let t9;
  if ($[13] !== t8) {
    t9 = sessionMediaUrl(t8);
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const transcriptUrl = t9;
  media?.recording && typeof media.recording === "object" ? media.recording.expiresAt : null;
  let t10;
  if ($[15] !== session.scheduledAt) {
    t10 = formatWhenParts(session.scheduledAt);
    $[15] = session.scheduledAt;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const scheduledParts = t10;
  const durationLabel = session.approximateLengthMinutes != null ? `${session.approximateLengthMinutes} min` : "\u2014";
  const [proposeOpen, setProposeOpen] = useState(false);
  let t11;
  if ($[17] !== canConfirm || $[18] !== counterpartLabel || $[19] !== onPropose || $[20] !== proposeLoading || $[21] !== session || $[22] !== waitingOnCounterpart) {
    t11 = {
      canConfirm,
      counterpartLabel,
      loading: proposeLoading,
      session,
      waitingOnCounterpart,
      onPropose
    };
    $[17] = canConfirm;
    $[18] = counterpartLabel;
    $[19] = onPropose;
    $[20] = proposeLoading;
    $[21] = session;
    $[22] = waitingOnCounterpart;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const proposeTimesProps = t11;
  let t12;
  if ($[24] !== session.suggestedTime1 || $[25] !== session.suggestedTime2 || $[26] !== session.suggestedTime3) {
    t12 = [session.suggestedTime1, session.suggestedTime2, session.suggestedTime3].filter(_temp);
    $[24] = session.suggestedTime1;
    $[25] = session.suggestedTime2;
    $[26] = session.suggestedTime3;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const suggestedTimes = t12;
  let t13;
  bb0: {
    if (session.finalCostUsd == null) {
      t13 = null;
      break bb0;
    }
    const gross = Number(session.finalCostUsd);
    const feePercent = Number(session.platformFeePercent ?? 15);
    const platformFee = session.platformFeeAmount != null ? Number(session.platformFeeAmount) : Math.round(gross * (feePercent / 100) * 100) / 100;
    const expertShare = session.expertShareAmount != null ? Number(session.expertShareAmount) : Math.round((gross - platformFee) * 100) / 100;
    const t14 = 100 - feePercent;
    let t15;
    if ($[28] !== expertShare || $[29] !== feePercent || $[30] !== gross || $[31] !== platformFee || $[32] !== t14) {
      t15 = {
        gross,
        platformFee,
        expertShare,
        feePercent,
        expertPercent: t14
      };
      $[28] = expertShare;
      $[29] = feePercent;
      $[30] = gross;
      $[31] = platformFee;
      $[32] = t14;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    t13 = t15;
  }
  const expertReceipt = t13;
  const proposedTimesDescription = canConfirm ? "Accept a slot, or propose different times below." : waitingOnCounterpart ? `Waiting for the ${counterpartLabel} to accept one of these times (or propose alternatives). Only they can accept a slot from this proposal.` : undefined;
  const cancelDescription = canConfirm && role === "EXPERT" ? "Decline this session request. The founder\u2019s card hold will be released." : session.statusKey === "schedule_confirmed" || session.statusKey === "live" ? "Cancel before anyone joins for a full refund. After someone joins, the package is kept." : "Cancel this session. Any authorized card hold will be released.";
  const cancelLabel = canConfirm && role === "EXPERT" ? "Decline request" : "Cancel session";
  let t14;
  if ($[34] !== chatPanel) {
    t14 = chatPanel ? <Card.Root boxShadow="sm" overflow="hidden" variant="subtle"><Box height={{
        base: "420px",
        lg: "520px"
      }}>{chatPanel}</Box></Card.Root> : null;
    $[34] = chatPanel;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const chatCard = t14;
  let t15;
  if ($[36] !== advisorSessionId || $[37] !== ics) {
    t15 = () => {
      if (!ics?.content) {
        return;
      }
      downloadTextFile(ics.fileName || `advisor-session-${advisorSessionId}.ics`, ics.content, "text/calendar;charset=utf-8");
    };
    $[36] = advisorSessionId;
    $[37] = ics;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const handleDownloadIcs = t15;
  let t16;
  if ($[39] !== durationLabel) {
    t16 = <MetaItem label="Duration">{durationLabel}</MetaItem>;
    $[39] = durationLabel;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== session.hourlyRate) {
    t17 = formatHourlyRate(session.hourlyRate) || "\u2014";
    $[41] = session.hourlyRate;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17) {
    t18 = <MetaItem label="Rate">{t17}</MetaItem>;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== session.requestedAt) {
    t19 = formatWhen(session.requestedAt);
    $[45] = session.requestedAt;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t19) {
    t20 = <MetaItem label="Requested">{t19}</MetaItem>;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== session.estimatedCostUsd) {
    t21 = session.estimatedCostUsd != null ? formatUsd(session.estimatedCostUsd) : "\u2014";
    $[49] = session.estimatedCostUsd;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t21) {
    t22 = <MetaItem label="Estimated">{t21}</MetaItem>;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t16 || $[54] !== t18 || $[55] !== t20 || $[56] !== t22) {
    t23 = <SimpleGrid columns={2} gap={4}>{t16}{t18}{t20}{t22}</SimpleGrid>;
    $[53] = t16;
    $[54] = t18;
    $[55] = t20;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== session.cancelationReason) {
    t24 = session.cancelationReason && <><Separator /><MetaItem label="Cancel reason">{session.cancelationReason}</MetaItem></>;
    $[58] = session.cancelationReason;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t23 || $[61] !== t24) {
    t25 = <DetailCard label="Details">{t23}{t24}</DetailCard>;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  const detailsCard = t25;
  let t26;
  if ($[63] !== handleDownloadIcs || $[64] !== ics?.content || $[65] !== isParty || $[66] !== isScheduled || $[67] !== proposeOpen) {
    t26 = !isParty ? null : <HStack flexShrink={0} gap={2} wrap="wrap">{isScheduled && <Button aria-expanded={proposeOpen} flexShrink={0} size="sm" variant={proposeOpen ? "subtle" : "outline"} onClick={() => setProposeOpen(_temp2)}><LuCalendarClock /> Reschedule</Button>}{ics?.content && <Button flexShrink={0} size="sm" variant="outline" onClick={handleDownloadIcs}><LuCalendarPlus /> Add to calendar</Button>}</HStack>;
    $[63] = handleDownloadIcs;
    $[64] = ics?.content;
    $[65] = isParty;
    $[66] = isScheduled;
    $[67] = proposeOpen;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  const whenActions = t26;
  let t27;
  if ($[69] !== counterpartLabel || $[70] !== durationLabel || $[71] !== isParty || $[72] !== scheduledParts || $[73] !== suggestedTimes || $[74] !== waitingOnCounterpart) {
    t27 = <VStack align="stretch" gap={1} minWidth={0}>{scheduledParts ? <><Heading size="xl">{scheduledParts.date}</Heading><Text color="colorPalette.fg/70" fontFamily="longform" fontSize="md">{scheduledParts.time} · {durationLabel}</Text></> : <><Heading size="lg">Not confirmed yet</Heading><Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{suggestedTimes.length ? `${suggestedTimes.length} proposed time${suggestedTimes.length === 1 ? "" : "s"} below` : !isParty ? "No time proposed yet." : waitingOnCounterpart ? `Waiting for the ${counterpartLabel} to pick a time.` : "Propose times to get this on the calendar."}</Text></>}</VStack>;
    $[69] = counterpartLabel;
    $[70] = durationLabel;
    $[71] = isParty;
    $[72] = scheduledParts;
    $[73] = suggestedTimes;
    $[74] = waitingOnCounterpart;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== t27 || $[77] !== whenActions) {
    t28 = <HStack align="flex-start" gap={4} justify="space-between" wrap="wrap">{t27}{whenActions}</HStack>;
    $[76] = t27;
    $[77] = whenActions;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  let t29;
  if ($[79] !== isScheduled || $[80] !== proposeOpen || $[81] !== proposeTimesProps) {
    t29 = isScheduled && proposeOpen && <><Separator /><ProposeTimesForm {...proposeTimesProps} /></>;
    $[79] = isScheduled;
    $[80] = proposeOpen;
    $[81] = proposeTimesProps;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== t28 || $[84] !== t29) {
    t30 = <DetailCard label="When">{t28}{t29}</DetailCard>;
    $[83] = t28;
    $[84] = t29;
    $[85] = t30;
  } else {
    t30 = $[85];
  }
  const whenCard = t30;
  let t31;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      base: 1,
      sm: 2
    };
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  const t32 = role === "EXPERT";
  let t33;
  if ($[87] !== session.expertUser || $[88] !== t32) {
    t33 = <PersonTile fallback="Advisor" isYou={t32} label="Expert" user={session.expertUser} />;
    $[87] = session.expertUser;
    $[88] = t32;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  const t34 = role === "SEEKER";
  let t35;
  if ($[90] !== session.seekerUser || $[91] !== t34) {
    t35 = <PersonTile fallback="Founder" isYou={t34} label="Founder" user={session.seekerUser} />;
    $[90] = session.seekerUser;
    $[91] = t34;
    $[92] = t35;
  } else {
    t35 = $[92];
  }
  let t36;
  if ($[93] !== t33 || $[94] !== t35) {
    t36 = <DetailCard label="People"><SimpleGrid columns={t31} gap={3}>{t33}{t35}</SimpleGrid></DetailCard>;
    $[93] = t33;
    $[94] = t35;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  let t37;
  if ($[96] !== canConfirm || $[97] !== confirmLoading || $[98] !== isNegotiating || $[99] !== isParty || $[100] !== onConfirm || $[101] !== proposedTimesDescription || $[102] !== suggestedTimes) {
    t37 = isNegotiating && <DetailCard description={isParty ? proposedTimesDescription : undefined} label={canConfirm ? "Accept a proposed time" : "Proposed times"}>{suggestedTimes.length === 0 ? <Text color="colorPalette.fg/50" fontSize="sm">No proposed times yet{canConfirm ? " \u2014 use Propose different times below." : "."}</Text> : <VStack align="stretch" gap={2}>{suggestedTimes.map(time_0 => {
          const parts = formatWhenParts(time_0);
          return <HStack key={time_0} align="center" borderColor="colorPalette.border" borderRadius="l2" borderWidth="1px" gap={3} justify="space-between" paddingX={4} paddingY={3} wrap="wrap"><VStack align="stretch" gap={0.5} minWidth={0}><Text fontWeight="semibold">{parts?.date || formatWhen(time_0)}</Text>{parts && <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{parts.time}</Text>}</VStack>{canConfirm && <Button loading={confirmLoading} size="sm" variant="cta" onClick={() => onConfirm(time_0)}><LuCheck /> Accept</Button>}</HStack>;
        })}</VStack>}</DetailCard>;
    $[96] = canConfirm;
    $[97] = confirmLoading;
    $[98] = isNegotiating;
    $[99] = isParty;
    $[100] = onConfirm;
    $[101] = proposedTimesDescription;
    $[102] = suggestedTimes;
    $[103] = t37;
  } else {
    t37 = $[103];
  }
  let t38;
  if ($[104] !== isCompleted || $[105] !== recordingUrl || $[106] !== transcriptUrl) {
    t38 = (isCompleted || recordingUrl || transcriptUrl) && <DetailCard label="Recording & transcript"><VStack align="stretch" gap={4}>{recordingUrl ? <VStack align="stretch" gap={2}><Text fontSize="sm" fontWeight="medium">Audio recording</Text><chakra.audio controls={true} src={recordingUrl} width="full" /></VStack> : <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">Recording usually lands a minute or two after the call ends. Refresh this page if it is still missing.</Text>}{transcriptUrl ? <Button asChild={true} alignSelf="flex-start" size="sm" variant="outline"><a href={transcriptUrl} rel="noreferrer" target="_blank"><LuFileText /> Open transcript</a></Button> : isCompleted ? <Text color="colorPalette.fg/50" fontSize="xs">Transcript appears here when Stream finishes processing.</Text> : null}</VStack></DetailCard>;
    $[104] = isCompleted;
    $[105] = recordingUrl;
    $[106] = transcriptUrl;
    $[107] = t38;
  } else {
    t38 = $[107];
  }
  let t39;
  if ($[108] !== isParty || $[109] !== isScheduled || $[110] !== isTerminal || $[111] !== proposeTimesProps) {
    t39 = isParty && !isTerminal && !isScheduled && <ProposeTimesCard {...proposeTimesProps} />;
    $[108] = isParty;
    $[109] = isScheduled;
    $[110] = isTerminal;
    $[111] = proposeTimesProps;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  let t40;
  if ($[113] !== cancelDescription || $[114] !== cancelLabel || $[115] !== cancelLoading || $[116] !== cancelReason || $[117] !== isParty || $[118] !== isTerminal || $[119] !== onCancel || $[120] !== onCancelReasonChange) {
    t40 = isParty && !isTerminal && <DetailCard description={cancelDescription} label={cancelLabel}><Textarea placeholder="Optional reason" rows={2} value={cancelReason} onChange={event => onCancelReasonChange(event.target.value)} /><Button alignSelf="flex-start" colorPalette="red" loading={cancelLoading} variant="outline" onClick={onCancel}><LuX /> {cancelLabel}</Button></DetailCard>;
    $[113] = cancelDescription;
    $[114] = cancelLabel;
    $[115] = cancelLoading;
    $[116] = cancelReason;
    $[117] = isParty;
    $[118] = isTerminal;
    $[119] = onCancel;
    $[120] = onCancelReasonChange;
    $[121] = t40;
  } else {
    t40 = $[121];
  }
  let t41;
  if ($[122] !== t36 || $[123] !== t37 || $[124] !== t38 || $[125] !== t39 || $[126] !== t40) {
    t41 = <>{t36}{t37}{t38}{t39}{t40}</>;
    $[122] = t36;
    $[123] = t37;
    $[124] = t38;
    $[125] = t39;
    $[126] = t40;
    $[127] = t41;
  } else {
    t41 = $[127];
  }
  const mainColumnCards = t41;
  let t42;
  if ($[128] !== expertReceipt || $[129] !== role) {
    t42 = expertReceipt && role === "EXPERT" && <DetailCard label="Your receipt"><VStack align="stretch" gap={3}><ReceiptRow label="Session" value={formatUsd(expertReceipt.gross)} /><ReceiptRow label={`Platform fee (${expertReceipt.feePercent}%)`} value={formatUsd(expertReceipt.platformFee)} /><Separator /><ReceiptRow emphasize={true} label={`You receive (${expertReceipt.expertPercent}%)`} value={formatUsd(expertReceipt.expertShare)} /></VStack></DetailCard>;
    $[128] = expertReceipt;
    $[129] = role;
    $[130] = t42;
  } else {
    t42 = $[130];
  }
  let t43;
  if ($[131] !== role || $[132] !== session.estimatedCostUsd || $[133] !== session.finalCostUsd) {
    t43 = session.finalCostUsd != null && role !== "EXPERT" && <DetailCard label="Cost"><ReceiptRow emphasize={true} label="Final cost" value={formatUsd(session.finalCostUsd)} />{session.estimatedCostUsd != null && <Text color="colorPalette.fg/50" fontSize="xs">Estimated {formatUsd(session.estimatedCostUsd)} before the call</Text>}</DetailCard>;
    $[131] = role;
    $[132] = session.estimatedCostUsd;
    $[133] = session.finalCostUsd;
    $[134] = t43;
  } else {
    t43 = $[134];
  }
  let t44;
  if ($[135] !== canRate || $[136] !== isCompleted || $[137] !== isParty || $[138] !== myRating || $[139] !== onRate) {
    t44 = isParty && isCompleted && canRate && <DetailCard label="Your rating">{myRating ? <VStack align="stretch" gap={3}><HStack gap={1}>{Array.from({
            length: 5
          }, (_, index) => <Icon key={index} boxSize={4} color={index < (myRating.score ?? 0) ? "yellow.fg" : "colorPalette.fg/25"} fill={index < (myRating.score ?? 0) ? "currentColor" : "none"}><LuStar /></Icon>)}<Text color="colorPalette.fg/60" fontSize="sm">{myRating.score}/5</Text></HStack>{myRating.note && <Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm">{myRating.note}</Text>}<Button alignSelf="flex-start" size="xs" variant="outline" onClick={onRate}>Update</Button></VStack> : <Button alignSelf="flex-start" size="sm" variant="outline" onClick={onRate}><LuStar /> Rate this session</Button>}</DetailCard>;
    $[135] = canRate;
    $[136] = isCompleted;
    $[137] = isParty;
    $[138] = myRating;
    $[139] = onRate;
    $[140] = t44;
  } else {
    t44 = $[140];
  }
  let t45;
  if ($[141] !== t42 || $[142] !== t43 || $[143] !== t44) {
    t45 = <>{t42}{t43}{t44}</>;
    $[141] = t42;
    $[142] = t43;
    $[143] = t44;
    $[144] = t45;
  } else {
    t45 = $[144];
  }
  const asideCards = t45;
  let t46;
  if ($[145] !== statusKey) {
    t46 = sessionStatusPalette(statusKey);
    $[145] = statusKey;
    $[146] = t46;
  } else {
    t46 = $[146];
  }
  const t47 = session.status?.title || statusKey;
  let t48;
  if ($[147] !== t46 || $[148] !== t47) {
    t48 = <Badge colorPalette={t46} variant="subtle">{t47}</Badge>;
    $[147] = t46;
    $[148] = t47;
    $[149] = t48;
  } else {
    t48 = $[149];
  }
  let t49;
  if ($[150] !== isParty || $[151] !== role) {
    t49 = isParty ? <Badge variant="outline">You are the {role === "EXPERT" ? "expert" : "founder"}</Badge> : <Badge colorPalette="gray" variant="outline">Read-only · not a party</Badge>;
    $[150] = isParty;
    $[151] = role;
    $[152] = t49;
  } else {
    t49 = $[152];
  }
  let t50;
  if ($[153] !== canConfirm || $[154] !== isTerminal) {
    t50 = canConfirm && !isTerminal && <Badge colorPalette="yellow" variant="subtle">Waiting on you</Badge>;
    $[153] = canConfirm;
    $[154] = isTerminal;
    $[155] = t50;
  } else {
    t50 = $[155];
  }
  let t51;
  if ($[156] !== t48 || $[157] !== t49 || $[158] !== t50) {
    t51 = <HStack gap={2} wrap="wrap">{t48}{t49}{t50}</HStack>;
    $[156] = t48;
    $[157] = t49;
    $[158] = t50;
    $[159] = t51;
  } else {
    t51 = $[159];
  }
  let t52;
  if ($[160] !== bookAgainHref || $[161] !== canInviteToBoard || $[162] !== canRate || $[163] !== inboxHref || $[164] !== isCompleted || $[165] !== isParty || $[166] !== myRating || $[167] !== onInviteToBoard || $[168] !== onRate || $[169] !== opportunitiesHref || $[170] !== role || $[171] !== sessionSettingsHref) {
    t52 = isParty && isCompleted && <Card.Root variant="subtle"><Card.Body gap={3}><Text fontWeight="semibold">This session is complete</Text><Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm">{canRate && !myRating ? "Rate the call, then pick a next step. A paid session is not a board seat." : role === "EXPERT" ? "Next: more opportunities, or update the rates founders see when they book you." : "Book another paid hour, or invite this advisor to a board seat \u2014 those are separate paths."}</Text><HStack gap={2} wrap="wrap">{canRate && !myRating ? <Button size="sm" variant="cta" onClick={onRate}><LuStar /> Rate this session</Button> : null}{role === "SEEKER" && bookAgainHref ? <Button asChild={true} size="sm" variant={canRate && !myRating ? "outline" : "cta"}><NextLink href={bookAgainHref}>Book again</NextLink></Button> : null}{role === "SEEKER" && canInviteToBoard && onInviteToBoard ? <PaywalledAdvisorAction intent="advisor-invite" upgradeCtaButtonProps={{
            size: "sm"
          }} upgradeCtaLabel="Upgrade to invite"><Button size="sm" variant="outline" onClick={onInviteToBoard}>Invite to board</Button></PaywalledAdvisorAction> : null}{role === "SEEKER" && inboxHref ? <Button asChild={true} size="sm" variant="ghost"><NextLink href={inboxHref}>Open Messages</NextLink></Button> : null}{role === "EXPERT" ? <><Button asChild={true} size="sm" variant="cta"><NextLink href={opportunitiesHref}>View opportunities</NextLink></Button><Button asChild={true} size="sm" variant="outline"><NextLink href={sessionSettingsHref}>Session rates</NextLink></Button></> : null}</HStack></Card.Body></Card.Root>;
    $[160] = bookAgainHref;
    $[161] = canInviteToBoard;
    $[162] = canRate;
    $[163] = inboxHref;
    $[164] = isCompleted;
    $[165] = isParty;
    $[166] = myRating;
    $[167] = onInviteToBoard;
    $[168] = onRate;
    $[169] = opportunitiesHref;
    $[170] = role;
    $[171] = sessionSettingsHref;
    $[172] = t52;
  } else {
    t52 = $[172];
  }
  let t53;
  if ($[173] !== canConfirm || $[174] !== isTerminal || $[175] !== role) {
    t53 = canConfirm && !isTerminal && <Card.Root colorPalette="yellow" variant="subtle"><Card.Body><HStack align="flex-start" gap={3}><Icon boxSize={5} color="yellow.fg" marginTop={0.5}><LuClock /></Icon><VStack align="stretch" gap={1}><Text fontWeight="semibold">Action required</Text><Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm">{role === "EXPERT" ? "The founder suggested times below. Accept one, propose different times, or decline." : "The advisor suggested times below. Accept one, propose different times, or cancel."}</Text></VStack></HStack></Card.Body></Card.Root>;
    $[173] = canConfirm;
    $[174] = isTerminal;
    $[175] = role;
    $[176] = t53;
  } else {
    t53 = $[176];
  }
  let t54;
  if ($[177] !== actionError) {
    t54 = actionError && <Text color="red.fg" fontFamily="longform" fontSize="sm">{actionError}</Text>;
    $[177] = actionError;
    $[178] = t54;
  } else {
    t54 = $[178];
  }
  let t55;
  if ($[179] !== isParty) {
    t55 = !isParty && <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">You can view this session but not act on it. Only the founder and the expert can schedule, cancel, join or chat.</Text>;
    $[179] = isParty;
    $[180] = t55;
  } else {
    t55 = $[180];
  }
  let t56;
  if ($[181] !== asideCards || $[182] !== chatCard || $[183] !== detailsCard || $[184] !== isNegotiating || $[185] !== isParty || $[186] !== isScheduled || $[187] !== mainColumnCards || $[188] !== whenCard) {
    t56 = isNegotiating && !chatCard ? <VStack align="stretch" gap={5}>{detailsCard}{whenCard}{mainColumnCards}</VStack> : isNegotiating ? <Grid alignItems="start" gap={5} templateColumns={{
      base: "1fr",
      lg: "minmax(0, 1.25fr) minmax(300px, 1fr)"
    }}><VStack align="stretch" gap={5}>{detailsCard}{whenCard}{mainColumnCards}</VStack><Box position={{
        lg: "sticky"
      }} top={4}>{chatCard}</Box></Grid> : <Grid alignItems="start" gap={5} templateColumns={{
      base: "1fr",
      lg: "minmax(0, 1.45fr) minmax(260px, 0.9fr)"
    }}><VStack align="stretch" gap={5}>{whenCard}{mainColumnCards}</VStack><VStack align="stretch" gap={5}>{chatCard}{detailsCard}{isScheduled && isParty && <SessionDeviceCheck />}{asideCards}</VStack></Grid>;
    $[181] = asideCards;
    $[182] = chatCard;
    $[183] = detailsCard;
    $[184] = isNegotiating;
    $[185] = isParty;
    $[186] = isScheduled;
    $[187] = mainColumnCards;
    $[188] = whenCard;
    $[189] = t56;
  } else {
    t56 = $[189];
  }
  let t57;
  if ($[190] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <Separator />;
    $[190] = t57;
  } else {
    t57 = $[190];
  }
  let t58;
  if ($[191] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <LuArrowLeft />;
    $[191] = t58;
  } else {
    t58 = $[191];
  }
  let t59;
  if ($[192] !== sessionsHref) {
    t59 = <Button asChild={true} alignSelf="flex-start" variant="ghost"><NextLink href={sessionsHref}>{t58} All sessions</NextLink></Button>;
    $[192] = sessionsHref;
    $[193] = t59;
  } else {
    t59 = $[193];
  }
  let t60;
  if ($[194] !== t51 || $[195] !== t52 || $[196] !== t53 || $[197] !== t54 || $[198] !== t55 || $[199] !== t56 || $[200] !== t59) {
    t60 = <VStack align="stretch" gap={5}>{t51}{t52}{t53}{t54}{t55}{t56}{t57}{t59}</VStack>;
    $[194] = t51;
    $[195] = t52;
    $[196] = t53;
    $[197] = t54;
    $[198] = t55;
    $[199] = t56;
    $[200] = t59;
    $[201] = t60;
  } else {
    t60 = $[201];
  }
  return t60;
};
const SESSION_CHAT_ASIDE_ID = 'advisors-session-chat-aside';

/**
 * Opens and closes the session chat aside: a chat icon while it is closed, a close-panel icon while
 * it is open. Renders inside the session's NestedAppLayout (the header is in its main panel), so it
 * reads that layout's panel state. On narrow screens the aside is a drawer and this opens it.
 */
export const SessionChatToggle = () => {
  const $ = _c(11);
  const layout = useParentAppLayout();
  let t0;
  if ($[0] !== layout) {
    t0 = layout.isPanelOpen?.(SESSION_CHAT_ASIDE_ID) ?? false;
    $[0] = layout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isOpen = t0;
  const label = isOpen ? "Close session chat" : "Open session chat";
  let t1;
  if ($[2] !== layout) {
    t1 = () => layout.togglePanel?.(SESSION_CHAT_ASIDE_ID);
    $[2] = layout;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isOpen) {
    t2 = isOpen ? <LuPanelRightClose /> : <LuMessageSquare />;
    $[4] = isOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isOpen || $[7] !== label || $[8] !== t1 || $[9] !== t2) {
    t3 = <IconButton aria-label={label} aria-pressed={isOpen} borderRadius="md" boxShadow="none" size="sm" title={label} variant="outlineGhost" onClick={t1}>{t2}</IconButton>;
    $[6] = isOpen;
    $[7] = label;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
export const SessionDetailSection = () => {
  const $ = _c(142);
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const t0 = String(router.query.advisorSessionId || "");
  let t1;
  if ($[0] !== t0) {
    t1 = t0.trim();
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const advisorSessionId = t1;
  const [cancelReason, setCancelReason] = useState("");
  const [actionError, setActionError] = useState(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  let t2;
  if ($[2] !== advisorSessionId) {
    t2 = {
      advisorSessionId
    };
    $[2] = advisorSessionId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !router.isReady || !advisorSessionId;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      variables: t2,
      skip: t3,
      fetchPolicy: "network-only"
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useQuery(ADVISOR_SESSION, t4);
  const session = query.data?.advisorSession;
  const isCompleted = session?.statusKey === SESSION_STATUS.completed;
  let t5;
  if ($[7] !== advisorSessionId) {
    t5 = {
      advisorSessionId
    };
    $[7] = advisorSessionId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = !advisorSessionId;
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = {
      variables: t5,
      skip: t6,
      errorPolicy: "all",
      fetchPolicy: "cache-and-network"
    };
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const mediaQuery = useQuery(ADVISOR_SESSION_MEDIA, t7);
  const media = mediaQuery.data?.advisorSession as SessionMedia | undefined;
  let t8;
  if ($[12] !== media?.recording || $[13] !== media?.transcript) {
    t8 = sessionMediaUrl(media?.recording) || sessionMediaUrl(media?.transcript);
    $[12] = media?.recording;
    $[13] = media?.transcript;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const hasSessionMedia = !!t8;
  let t9;
  if ($[15] !== advisorSessionId || $[16] !== hasSessionMedia || $[17] !== isCompleted || $[18] !== mediaQuery) {
    t9 = () => {
      if (!isCompleted || hasSessionMedia || !advisorSessionId) {
        return;
      }
      const timer = window.setInterval(() => {
        mediaQuery.refetch();
      }, 8000);
      return () => window.clearInterval(timer);
    };
    $[15] = advisorSessionId;
    $[16] = hasSessionMedia;
    $[17] = isCompleted;
    $[18] = mediaQuery;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== advisorSessionId || $[21] !== hasSessionMedia || $[22] !== isCompleted || $[23] !== mediaQuery.refetch) {
    t10 = [advisorSessionId, hasSessionMedia, isCompleted, mediaQuery.refetch];
    $[20] = advisorSessionId;
    $[21] = hasSessionMedia;
    $[22] = isCompleted;
    $[23] = mediaQuery.refetch;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  useEffect(t9, t10);
  const role = session?.currentUserRole;
  let t11;
  if ($[25] !== role) {
    t11 = isSessionParty(role);
    $[25] = role;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const isParty = t11;
  let t12;
  if ($[27] !== advisorSessionId || $[28] !== isParty || $[29] !== paths.sessions || $[30] !== session) {
    t12 = advisorsBreadcrumbsWithTrail({
      title: session && !isParty ? "Sessions" : "My sessions",
      href: paths.sessions
    }, {
      title: "Session",
      href: advisorSessionId ? `${paths.sessions}/${advisorSessionId}` : paths.sessions
    });
    $[27] = advisorSessionId;
    $[28] = isParty;
    $[29] = paths.sessions;
    $[30] = session;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12) {
    t13 = {
      breadcrumbs: t12
    };
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  useAdvisorsToolbar(t13);
  const canJoin = isParty && isJoinableSession(session?.statusKey);
  const canRate = !!session?.canRate;
  const myRating = session?.currentUserRating;
  const counterpartLabel = role === "EXPERT" ? "founder" : "advisor";
  const t14 = role === "EXPERT" ? session?.seekerUser : session?.expertUser;
  const t15 = counterpartLabel === "founder" ? "Founder" : "Advisor";
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = personName(t14, t15);
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const counterpartName = t16;
  const roomHref = `${paths.sessions}/${advisorSessionId}/room`;
  let t17;
  if ($[37] !== isCompleted || $[38] !== isParty || $[39] !== session) {
    t17 = isParty && !!session && (!isTerminalSession(session.statusKey) || isCompleted);
    $[37] = isCompleted;
    $[38] = isParty;
    $[39] = session;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const showChat = t17;
  const expertUserId = session?.expertUserId || session?.expertUser?.userId;
  const canBookAgain = role === "SEEKER" && !!session?.advisorProfile?.canAcceptSessions && !!expertUserId;
  let t18;
  if ($[41] !== canBookAgain || $[42] !== expertUserId || $[43] !== paths) {
    t18 = canBookAgain ? paths.withQuery(paths.sessionNew, {
      expertUserId
    }) : null;
    $[41] = canBookAgain;
    $[42] = expertUserId;
    $[43] = paths;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const bookAgainHref = t18;
  const hasBoardSeat = !!session?.boardEngagement?.advisorEngagementId;
  const canInviteToBoard = role === "SEEKER" && !!expertUserId && !hasBoardSeat;
  let t19;
  if ($[45] !== expertUserId || $[46] !== hasBoardSeat || $[47] !== paths) {
    t19 = hasBoardSeat ? paths.withQuery(paths.inbox, {
      advisorUserId: expertUserId
    }) : null;
    $[45] = expertUserId;
    $[46] = hasBoardSeat;
    $[47] = paths;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const inboxHref = t19;
  let t20;
  let t21;
  if ($[49] !== advisorSessionId || $[50] !== canRate || $[51] !== isCompleted || $[52] !== myRating || $[53] !== router.isReady || $[54] !== router.query.rate || $[55] !== session) {
    t20 = () => {
      if (!router.isReady || !session || !advisorSessionId) {
        return;
      }
      if (!isCompleted || !canRate || myRating) {
        return;
      }
      const wantRate = String(router.query.rate || "") === "1";
      const storageKey = `sco.advisors.sessionRatePrompted.${advisorSessionId}`;
      const alreadyPrompted = typeof window !== "undefined" && window.sessionStorage.getItem(storageKey) === "1";
      if (!wantRate && alreadyPrompted) {
        return;
      }
      setRateOpen(true);
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(storageKey, "1");
      }
    };
    t21 = [advisorSessionId, canRate, isCompleted, myRating, router.isReady, router.query.rate, session];
    $[49] = advisorSessionId;
    $[50] = canRate;
    $[51] = isCompleted;
    $[52] = myRating;
    $[53] = router.isReady;
    $[54] = router.query.rate;
    $[55] = session;
    $[56] = t20;
    $[57] = t21;
  } else {
    t20 = $[56];
    t21 = $[57];
  }
  useEffect(t20, t21);
  let t22;
  if ($[58] !== advisorSessionId) {
    const refetchSession = [{
      query: ADVISOR_SESSION,
      variables: {
        advisorSessionId
      }
    }];
    t22 = [...refetchSession, ADVISORS_ATTENTION_REFETCH];
    $[58] = advisorSessionId;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  const refetchSessionAndBadges = t22;
  let t23;
  if ($[60] !== refetchSessionAndBadges) {
    t23 = {
      refetchQueries: refetchSessionAndBadges
    };
    $[60] = refetchSessionAndBadges;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  const [confirmSchedule, confirmState] = useMutation(CONFIRM_ADVISOR_SESSION_SCHEDULE, t23);
  let t24;
  if ($[62] !== refetchSessionAndBadges) {
    t24 = {
      refetchQueries: refetchSessionAndBadges
    };
    $[62] = refetchSessionAndBadges;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  const [proposeSchedule, proposeState] = useMutation(PROPOSE_ADVISOR_SESSION_SCHEDULE, t24);
  let t25;
  if ($[64] !== refetchSessionAndBadges) {
    t25 = {
      refetchQueries: refetchSessionAndBadges
    };
    $[64] = refetchSessionAndBadges;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  const [cancelSession, cancelState] = useMutation(CANCEL_ADVISOR_SESSION, t25);
  let t26;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = async (fn, fallback) => {
      setActionError(null);
      ;
      try {
        await fn();
      } catch (t27) {
        const err = t27;
        setActionError(cleanErrorMessage(err, fallback));
      }
    };
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  const run = t26;
  let t27;
  if ($[67] !== advisorSessionId || $[68] !== confirmSchedule || $[69] !== query) {
    t27 = async scheduledAt => {
      setActionError(null);
      ;
      try {
        await confirmSchedule({
          variables: {
            input: {
              advisorSessionId,
              scheduledAt
            }
          }
        });
      } catch (t28) {
        const err_0 = t28;
        if (bookingErrorCode(err_0) === "ADVISOR_SESSION_TIME_NOT_PROPOSED") {
          setActionError("That time is no longer one of the proposed times \u2014 the other party may have suggested new ones. The list below is refreshed; pick one of those or propose different times.");
          query.refetch();
          return;
        }
        setActionError(cleanErrorMessage(err_0, "Could not confirm that time."));
      }
    };
    $[67] = advisorSessionId;
    $[68] = confirmSchedule;
    $[69] = query;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  const handleConfirm = t27;
  let t28;
  if ($[71] !== advisorSessionId || $[72] !== proposeSchedule) {
    t28 = suggestedTimesIso => {
      if (!suggestedTimesIso.length) {
        setActionError("Pick at least one suggested time.");
        return;
      }
      run(() => proposeSchedule({
        variables: {
          input: {
            advisorSessionId,
            suggestedTimes: suggestedTimesIso
          }
        }
      }), "Could not send those times.");
    };
    $[71] = advisorSessionId;
    $[72] = proposeSchedule;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  const handlePropose = t28;
  let t29;
  if ($[74] !== advisorSessionId || $[75] !== cancelReason || $[76] !== cancelSession) {
    t29 = () => run(() => cancelSession({
      variables: {
        input: {
          advisorSessionId,
          reason: cancelReason.trim() || null
        }
      }
    }), "Could not cancel this session.");
    $[74] = advisorSessionId;
    $[75] = cancelReason;
    $[76] = cancelSession;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  const handleCancel = t29;
  if (!advisorSessionId && router.isReady) {
    let t30;
    if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <SectionScroll><Text color="colorPalette.fg/60">Missing session id.</Text></SectionScroll>;
      $[78] = t30;
    } else {
      t30 = $[78];
    }
    return t30;
  }
  let headerAction = null;
  if (canJoin) {
    let t30;
    if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <LuVideo />;
      $[79] = t30;
    } else {
      t30 = $[79];
    }
    let t31;
    if ($[80] !== roomHref) {
      t31 = <Button asChild={true} variant="cta"><NextLink href={roomHref}>{t30} Join session</NextLink></Button>;
      $[80] = roomHref;
      $[81] = t31;
    } else {
      t31 = $[81];
    }
    headerAction = t31;
  } else {
    if (isParty && isCompleted && canRate && !myRating) {
      let t30;
      if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
        t30 = <Button variant="cta" onClick={() => setRateOpen(true)}><LuStar /> Rate session</Button>;
        $[82] = t30;
      } else {
        t30 = $[82];
      }
      headerAction = t30;
    } else {
      if (isCompleted && bookAgainHref) {
        let t30;
        if ($[83] !== bookAgainHref) {
          t30 = <Button asChild={true} variant="cta"><NextLink href={bookAgainHref}>Book again</NextLink></Button>;
          $[83] = bookAgainHref;
          $[84] = t30;
        } else {
          t30 = $[84];
        }
        headerAction = t30;
      } else {
        if (isCompleted && canInviteToBoard) {
          let t30;
          if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
            t30 = <PaywalledAdvisorAction intent="advisor-invite" upgradeCtaLabel="Upgrade to invite"><Button variant="outline" onClick={() => setInviteOpen(true)}>Invite to board</Button></PaywalledAdvisorAction>;
            $[85] = t30;
          } else {
            t30 = $[85];
          }
          headerAction = t30;
        } else {
          if (isCompleted && role === "EXPERT") {
            let t30;
            if ($[86] !== paths.opportunities) {
              t30 = <Button asChild={true} variant="cta"><NextLink href={paths.opportunities}>View opportunities</NextLink></Button>;
              $[86] = paths.opportunities;
              $[87] = t30;
            } else {
              t30 = $[87];
            }
            headerAction = t30;
          }
        }
      }
    }
  }
  let t30;
  if ($[88] !== showChat) {
    t30 = showChat ? <SessionChatToggle /> : null;
    $[88] = showChat;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  const chatToggle = t30;
  let t31;
  if ($[90] !== chatToggle || $[91] !== headerAction) {
    t31 = headerAction && chatToggle ? <HStack gap={2}>{headerAction}{chatToggle}</HStack> : headerAction || chatToggle;
    $[90] = chatToggle;
    $[91] = headerAction;
    $[92] = t31;
  } else {
    t31 = $[92];
  }
  const sectionAction = t31;
  const t32 = session?.reason || "Paid video session";
  let t33;
  if ($[93] !== isParty || $[94] !== role || $[95] !== session) {
    t33 = !session ? "Session" : isParty ? `Session with ${personName(role === "EXPERT" ? session.seekerUser : session.expertUser)}` : `${personName(session.seekerUser, "Founder")} with ${personName(session.expertUser, "Advisor")}`;
    $[93] = isParty;
    $[94] = role;
    $[95] = session;
    $[96] = t33;
  } else {
    t33 = $[96];
  }
  let t34;
  if ($[97] !== sectionAction || $[98] !== t32 || $[99] !== t33) {
    t34 = <SectionHeader action={sectionAction} description={t32} eyebrow={"Advisors \xB7 Sessions"} title={t33} />;
    $[97] = sectionAction;
    $[98] = t32;
    $[99] = t33;
    $[100] = t34;
  } else {
    t34 = $[100];
  }
  const t35 = query.loading && !session;
  let t36;
  if ($[101] !== actionError || $[102] !== advisorSessionId || $[103] !== bookAgainHref || $[104] !== canInviteToBoard || $[105] !== cancelReason || $[106] !== cancelState || $[107] !== confirmState || $[108] !== handleCancel || $[109] !== handleConfirm || $[110] !== handlePropose || $[111] !== inboxHref || $[112] !== media || $[113] !== paths.opportunities || $[114] !== paths.sessionSettings || $[115] !== paths.sessions || $[116] !== proposeState || $[117] !== session) {
    t36 = session && <SessionDetailView actionError={actionError} advisorSessionId={advisorSessionId} bookAgainHref={bookAgainHref} cancelLoading={cancelState.loading} cancelReason={cancelReason} canInviteToBoard={canInviteToBoard} confirmLoading={confirmState.loading} inboxHref={inboxHref} media={media} opportunitiesHref={paths.opportunities} proposeLoading={proposeState.loading} session={session} sessionSettingsHref={paths.sessionSettings} sessionsHref={paths.sessions} onCancel={() => void handleCancel()} onCancelReasonChange={setCancelReason} onConfirm={time => void handleConfirm(time)} onInviteToBoard={() => setInviteOpen(true)} onPropose={handlePropose} onRate={() => setRateOpen(true)} />;
    $[101] = actionError;
    $[102] = advisorSessionId;
    $[103] = bookAgainHref;
    $[104] = canInviteToBoard;
    $[105] = cancelReason;
    $[106] = cancelState;
    $[107] = confirmState;
    $[108] = handleCancel;
    $[109] = handleConfirm;
    $[110] = handlePropose;
    $[111] = inboxHref;
    $[112] = media;
    $[113] = paths.opportunities;
    $[114] = paths.sessionSettings;
    $[115] = paths.sessions;
    $[116] = proposeState;
    $[117] = session;
    $[118] = t36;
  } else {
    t36 = $[118];
  }
  let t37;
  if ($[119] !== query.error || $[120] !== t35 || $[121] !== t36) {
    t37 = <AdvisorsQueryState error={query.error} loading={t35}>{t36}</AdvisorsQueryState>;
    $[119] = query.error;
    $[120] = t35;
    $[121] = t36;
    $[122] = t37;
  } else {
    t37 = $[122];
  }
  let t38;
  if ($[123] !== counterpartName || $[124] !== query || $[125] !== rateOpen || $[126] !== session) {
    t38 = session && <SessionRatingModal counterpartName={counterpartName} open={rateOpen} session={session} onOpenChange={setRateOpen} onRated={() => void query.refetch()} />;
    $[123] = counterpartName;
    $[124] = query;
    $[125] = rateOpen;
    $[126] = session;
    $[127] = t38;
  } else {
    t38 = $[127];
  }
  let t39;
  if ($[128] !== expertUserId || $[129] !== inviteOpen) {
    t39 = expertUserId && <InviteToBoardModal advisorUserId={expertUserId} isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />;
    $[128] = expertUserId;
    $[129] = inviteOpen;
    $[130] = t39;
  } else {
    t39 = $[130];
  }
  let t40;
  if ($[131] !== t34 || $[132] !== t37 || $[133] !== t38 || $[134] !== t39) {
    t40 = <SectionScroll maxWidth="5xl">{t34}{t37}{t38}{t39}</SectionScroll>;
    $[131] = t34;
    $[132] = t37;
    $[133] = t38;
    $[134] = t39;
    $[135] = t40;
  } else {
    t40 = $[135];
  }
  const content = t40;
  if (!showChat || !session) {
    return content;
  }
  let t41;
  if ($[136] !== advisorSessionId || $[137] !== session.scheduledAt) {
    t41 = <SessionChatPanel advisorSessionId={advisorSessionId} scheduledAt={session.scheduledAt} />;
    $[136] = advisorSessionId;
    $[137] = session.scheduledAt;
    $[138] = t41;
  } else {
    t41 = $[138];
  }
  let t42;
  if ($[139] !== content || $[140] !== t41) {
    t42 = <SessionWithChatLayout chatPanel={t41} content={content} />;
    $[139] = content;
    $[140] = t41;
    $[141] = t42;
  } else {
    t42 = $[141];
  }
  return t42;
};

/**
 * The session page with its chat as a right aside (a drawer below lg). `content` holds the header,
 * and so the SessionChatToggle, which must render inside this layout to reach the aside.
 */
export const SessionWithChatLayout = props => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...attachedPanelContainerProps,
      display: "flex",
      flexDirection: "column",
      minHeight: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.content) {
    t1 = {
      id: "advisors-session-detail-main",
      containerProps: t0,
      children: props.content
    };
    $[1] = props.content;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      base: true,
      lg: false
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props.chatPanel) {
    t3 = {
      id: SESSION_CHAT_ASIDE_ID,
      isPanelOpen: true,
      defaultWidth: 380,
      minWidth: 300,
      maxWidth: 560,
      isResizable: true,
      resizeHandlePosition: "left",
      useDrawerBreakpoints: t2,
      containerProps: attachedAsidePanelContainerProps,
      children: props.chatPanel
    };
    $[4] = props.chatPanel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <NestedAppLayout id="advisors-session-detail" panels={[t1, t3]} />;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
function _temp(time) {
  return !!time;
}
function _temp2(open) {
  return !open;
}